import { Button, ConfigProvider, Flex, Tag, Typography } from 'antd';
import dayjs from 'dayjs';

import { lightTheme } from '../../../theme';
import { formatDate } from '../../../ui/utils/format-date';
import { formatCredits, formatUsd } from '../shared/billing-format';

import { DiscountTags, DiscountedPrice } from './DiscountedPrice';

import type { GetBillingOverviewResponseDto } from '../../../api/generated/model/getBillingOverviewResponseDto';
import './YourPlanCard.css';

export type YourPlanCardProps = {
  overview: GetBillingOverviewResponseDto;
  portalLoading: boolean;
  resubscribeLoading: boolean;
  onManagePlan: () => void;
  onOpenPortal: () => void;
  onResubscribe: () => void;
};

export const YourPlanCard = ({
  overview,
  portalLoading,
  resubscribeLoading,
  onManagePlan,
  onOpenPortal,
  onResubscribe,
}: YourPlanCardProps) => {
  const { subscription, plan, credits, discounts, estimatedNextInvoiceCents, trialChargeCents } = overview;
  if (!subscription) {
    return null;
  }
  // With a discount the renewal figure matters more than the list price: say what the card will be charged.
  const nextCharge = (cents: number | null) =>
    discounts.length > 0 && cents !== null ? ` · ${formatUsd(cents)}` : '';

  const { status, cancelledAt, trialEndsAt, currentPeriodEnd } = subscription;
  const kind =
    status === 'cancelled' || status === 'suspended'
      ? 'ended'
      : status === 'past_due'
        ? 'past_due'
        : cancelledAt
          ? 'ending'
          : status;
  const monthly = `${formatCredits(plan.credits ?? 0)} credits a month`;
  const daysLeft = Math.max(0, Math.ceil(dayjs(trialEndsAt).diff(dayjs(), 'day', true)));
  const perks = 'Unlimited teammates, top-ups that never expire, and auto-recharge so your agents never stall.';

  const state = {
    ended: {
      badge: 'Plan ended',
      title: 'Your plan ended.',
      body: 'Re-subscribe to use your remaining credits again. Your saved card and top-up credits carry over.',
      action: 'Re-subscribe',
      onClick: onResubscribe,
      loading: resubscribeLoading,
      note: null,
    },
    past_due: {
      badge: 'Payment failed',
      title: 'Your last payment failed.',
      body: 'Update your card and your plan keeps going. Texting, calling and AI keep working from your remaining credits.',
      action: 'Update card',
      onClick: onOpenPortal,
      loading: portalLoading,
      note: null,
    },
    ending: {
      badge: `Ends ${dayjs(cancelledAt).format('MMM D')}`,
      title: `${monthly}.`,
      body: 'Your credits keep working until your plan ends. Reactivate any time.',
      action: 'Manage plan',
      onClick: onManagePlan,
      loading: false,
      note: `Plan ends ${formatDate(cancelledAt)}`,
    },
    trialing: {
      badge: `Trial · ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left`,
      title: `Go from ${formatCredits(credits.plan?.included ?? 0)} to ${monthly}.`,
      body: perks,
      action: 'Upgrade now',
      onClick: onManagePlan,
      loading: false,
      note: `Trial ends ${formatDate(trialEndsAt)}${nextCharge(trialChargeCents)}`,
    },
    active: {
      badge: 'Active',
      title: `${monthly}.`,
      body: perks,
      action: 'Manage plan',
      onClick: onManagePlan,
      loading: false,
      note: `Renews ${formatDate(currentPeriodEnd)}${nextCharge(estimatedNextInvoiceCents)}`,
    },
  }[kind];

  return (
    <ConfigProvider theme={{ token: { colorTextLightSolid: lightTheme.token.colorTextLightSolid } }}>
      <Flex
        align="center"
        gap={20}
        wrap
        data-testid={kind === 'ended' ? 'billing-plan-ended' : undefined}
        className={`your-plan-card your-plan-card--${kind}`}
      >
        <span className="your-plan-card-circle your-plan-card-circle--top" />
        <span className="your-plan-card-circle your-plan-card-circle--bottom" />

        <Flex vertical align="flex-start" gap={8} flex="1 1 240px" className="your-plan-card-content">
          <Tag variant="filled" data-testid="billing-plan-status" className="your-plan-card-badge">
            <span className="your-plan-card-badge-dot" />
            {state.badge}
          </Tag>
          <Typography.Text strong style={{ color: 'inherit', fontSize: 22 }}>
            {state.title}
          </Typography.Text>
          <Typography.Text
            style={{
              color: 'color-mix(in srgb, var(--ant-color-text-light-solid) 82%, transparent)',
              fontSize: 13,
            }}
          >
            {state.body}
            {state.body === perks && plan.priceCents !== null && (
              <>
                {' '}
                <DiscountedPrice
                  cents={plan.priceCents}
                  discountedCents={plan.discountedPriceCents}
                  suffix=" / month"
                  style={{ color: 'var(--ant-color-text-light-solid)' }}
                  testId="billing-plan-price"
                />{' '}
                · cancel anytime.
              </>
            )}
          </Typography.Text>
          {discounts.length > 0 && (
            <Flex gap={6} wrap>
              <DiscountTags discounts={discounts} detailed />
            </Flex>
          )}
        </Flex>

        <Flex vertical align="flex-end" gap={8} className="your-plan-card-content">
          <Button
            loading={state.loading}
            onClick={state.onClick}
            data-testid={
              kind === 'trialing'
                ? 'billing-upgrade'
                : kind === 'ended'
                  ? 'billing-resubscribe'
                  : undefined
            }
            className="your-plan-card-action"
            style={{ height: 'auto', padding: '11px 22px', fontWeight: 600 }}
          >
            {state.action}
          </Button>
          {state.note && (
            <Typography.Text
              style={{
                color: 'color-mix(in srgb, var(--ant-color-text-light-solid) 75%, transparent)',
                fontSize: 12,
              }}
            >
              {state.note}
            </Typography.Text>
          )}
        </Flex>
      </Flex>
    </ConfigProvider>
  );
};
